'use client';

/**
 * PlaceHeader — who, where, and how things stand. Two lines, one bar.
 *
 * WHAT CHANGED AND WHY. The header used to carry a chevron on the title AND a
 * tinted "ⓘ More about this place" pill on the subtitle line, both opening the
 * same sheet. On a merchant with a red accent that pill rendered as the loudest
 * element on the screen — louder than the restaurant's own name — and read as a
 * warning badge. Two controls, one destination, and the wrong one shouting.
 *
 * Below it sat a separate 34px SessionRibbon whose most common content was the
 * two words "Open now". A strip of permanent chrome, present on every screen,
 * to say something that fits next to the title as a dot.
 *
 * So the status line moved INTO the header as its second row, and it renders
 * only when it has something a guest could act on. The default menu now spends
 * 56px on chrome where it used to spend 90px, and the "is this place open"
 * answer sits where the guest is already looking: next to the name.
 *
 *   ┌──────────────────────────────────────┐
 *   │ [logo]  Sankofa Diner Osu  ›     ⌕ 👤 │   56px
 *   │         ● Open · Table 7             │   +18px, only when it speaks
 *   └──────────────────────────────────────┘
 *
 * The trailing slot holds SEARCH and YOU and nothing else. Those are the only
 * two destinations a single-screen menu has besides the menu itself, and they
 * live here now that the bottom rail is gone — the bottom of the screen belongs
 * to the guest's order, not to navigation. Language selection moved into the
 * You sheet: a flag dropdown is not a top-three control on a menu.
 */

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowLeft, ChevronRight, ReceiptText, Search, SlidersHorizontal, User, X } from 'lucide-react';
import { useState, useRef, useEffect, type ReactNode, type RefObject } from 'react';

import { cn } from '@/components/ui/utils';
import { zClass } from '@/lib/ui/z-layers';
import type { PlaceState } from '@/lib/customer/place-state';

/** One fulfilment choice in the guest's words, for the status-line picker. */
export type PlaceModeOption = {
  key: string;
  label: string;
  hint?: string | null;
};

/**
 * Shared mode-picker menu used by both PlaceHeaderStatus and PlaceModeSwitcher.
 *
 * A11y contract:
 *   – `role="menu"` / `role="menuitem"` (matches click-based interaction, not
 *     the arrow-key `listbox` pattern)
 *   – `aria-current="true"` on the active item (not `aria-selected`, which
 *     implies multi-select listbox)
 *   – Escape key closes the menu and returns focus to the trigger
 *   – Click outside closes the menu
 *   – First item receives focus when the menu opens
 */
function ModePickerMenu({
  modes,
  currentModeKey,
  onModeChange,
  onClose,
  triggerRef,
}: {
  modes: PlaceModeOption[];
  currentModeKey?: string | null;
  onModeChange: (key: string) => void;
  onClose: () => void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  // Focus the first item when the menu opens (keyboard a11y).
  useEffect(() => {
    firstItemRef.current?.focus();
  }, []);

  // Escape key closes and returns focus to the trigger. stopPropagation keeps
  // a simultaneously-open sheet (search, AI panel, orders) from also closing
  // on the same keypress.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, triggerRef]);

  // Click outside closes the menu. The setTimeout avoids the opening click
  // from immediately closing it (event propagation delay). Clicks on the
  // trigger are ignored — it handles its own toggle — and clicks anywhere
  // outside the OPTIONS GRID (not the full-width overlay, which covers the
  // content below the header) close the menu, so tapping a category tab or
  // the menu's own label dismisses it.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current && !menuRef.current.contains(target)) {
        onClose();
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose, triggerRef]);

  return (
    <div
      className={cn(
        'absolute inset-x-0 top-full z-[1]',
        'border-b border-[var(--color-border-subtle)]',
        'bg-[var(--color-bg-surface)]',
        'shadow-[0_16px_40px_rgba(0,0,0,0.18)]',
      )}
    >
      <div className="mx-auto w-full px-4 py-3 [max-width:var(--place-measure,100%)]">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
          How would you like this?
        </p>
        <div
          ref={menuRef}
          role="menu"
          aria-label="How would you like this?"
          className="mt-2 grid gap-2 sm:grid-cols-2"
        >
          {modes.map((mode, i) => (
            <button
              key={mode.key}
              ref={i === 0 ? firstItemRef : undefined}
              type="button"
              role="menuitem"
              aria-current={mode.key === currentModeKey ? 'true' : undefined}
              onClick={() => {
                onClose();
                // Return focus to the trigger after selection, per the menu
                // pattern — otherwise the unmount drops focus to <body>.
                triggerRef.current?.focus();
                onModeChange(mode.key);
              }}
              className={cn(
                'flex min-h-[3rem] flex-col items-start justify-center gap-0.5 rounded-xl border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)] px-3 py-2 text-left',
                'transition-colors hover:border-[var(--color-accent)]',
                'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
              )}
            >
              <span className="truncate text-sm font-semibold leading-tight">{mode.label}</span>
              {mode.hint ? (
                <span className="truncate text-[11px] leading-tight text-[var(--color-text-secondary)]">
                  {mode.hint}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export type PlaceHeaderProps = {
  title: string;
  subtitle?: string | null;
  backHref?: string;
  backAsClose?: boolean;
  /** Business logo — shown in the leading slot when there is no back control,
   *  so a guest who scanned a QR recognises whose menu they are on at a glance. */
  logoUrl?: string | null;
  /**
   * The guest's situation, rendered as the header's second line. Replaces the
   * standalone SessionRibbon. Pass null on screens where it would only repeat
   * what the page itself already says.
   */
  status?: PlaceState | null;
  /** Tapping the status line — e.g. opening the cart. Statement when absent. */
  onStatusAction?: (action: string) => void;
  /** Route root of the place, for a status line that navigates. */
  basePath?: string;
  onSearch?: () => void;
  /**
   * V2 only: the menu filter sheet is open, so the header control becomes its
   * close control. Search text remains applied when the sheet closes.
   */
  searchOpen?: boolean;
  /**
   * Opens the guest's own orders. A first-class control, NOT a row buried in
   * the You sheet: "where is the order I just placed" is the most common thing
   * a guest comes back to a menu for, and it used to take three taps (You →
   * scroll → My orders) with nothing on screen suggesting it existed.
   */
  onOrders?: () => void;
  /** How many orders this guest has on this device, for the count on that control. */
  ordersBadge?: number;
  onAccount?: () => void;
  onBranchInfo?: () => void;
  trailing?: ReactNode;
  className?: string;
  /**
   * The fulfilment modes the guest can switch to, shown in the status-line
   * picker when the branch offers more than one way to get food. The status
   * line itself becomes the switcher: it already says the current mode, so
   * tapping it to change is the same gesture in the same place.
   */
  modes?: PlaceModeOption[] | null;
  /** Chow proportions for the `?experience=v2` guest surface. */
  chow?: boolean;
  /** Called when the guest picks a mode from the status-line picker. */
  onModeChange?: (key: string) => void;
  /** The guest's current mode key, for marking the active option in the picker. */
  currentModeKey?: string | null;
  /**
   * The current fulfilment mode, rendered as the header's second line and made
   * tappable so the guest can switch modes. Passed whenever the branch offers
   * more than one way to get food AND the guest has picked one — this is the
   * persistent "change mode" control, independent of the action bar (which
   * only asks the question while the cart is empty).
   */
  modeSwitcher?: { label: string; detail?: string | null } | null;
};

export function PlaceHeader({
  title,
  subtitle,
  backHref,
  backAsClose = false,
  logoUrl,
  status,
  onStatusAction,
  basePath = '',
  onSearch,
  searchOpen = false,
  onOrders,
  ordersBadge = 0,
  onAccount,
  onBranchInfo,
  trailing,
  modes,
  onModeChange,
  currentModeKey,
  modeSwitcher,
  chow = false,
  className,
}: PlaceHeaderProps) {
  const router = useRouter();
  const BackIcon = backAsClose ? X : ArrowLeft;

  const showStatus = placeStatusVisible(status);

  return (
    <header
      className={cn(
        'sticky top-0 w-full',
        zClass('shellChrome'),
        // Chow's page header is the app's `<Header>`: a plain row on paper with
        // no hairline under it.
        !chow && 'border-b',
        chow
          ? 'bg-[hsl(var(--chow-paper))]'
          : 'border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)]/90 backdrop-blur-xl',
        'text-[var(--color-text-primary)]',
        'font-[family-name:var(--font-place-control)]',
        className,
      )}
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {/* The identity block below is a tap target (role=button), so the visible
          title cannot be an <h1>. This gives assistive tech the page's one
          top-level heading — the merchant's name — without changing the layout
          (QA 2026-10-20: /m/[slug] previously exposed zero <h1>). */}
      <h1 className="sr-only">{title}</h1>
      <div className={cn('mx-auto flex w-full items-center gap-1 px-2 [max-width:var(--place-measure,100%)]', chow ? 'h-16 gap-3 px-4 sm:px-4' : 'h-14')}>
        {backHref ? (
          <Link
            href={backHref}
            aria-label={backAsClose ? 'Close' : 'Back'}
            onClick={(event) => {
              if (window.history.length > 1) {
                event.preventDefault();
                router.back();
              }
            }}
            className={cn(placeIconButton, chow && 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]')}
          >
            <BackIcon aria-hidden="true" className="size-5" />
          </Link>
        ) : null}

        {/* Identity mark: a back link replaces the leading slot entirely; without
            one the business logo fills it so the page reads "whose place is this"
            even before the title. No logo → nothing (no dead spacer). */}
        {!chow && !backHref && logoUrl ? (
          <Image
            src={logoUrl}
            alt=""
            width={32}
            height={32}
            unoptimized
            className={cn('shrink-0 border border-[var(--color-border-subtle)] object-cover', chow ? 'size-10 rounded-xl' : 'size-9 rounded-lg')}
          />
        ) : null}

        {/* The whole identity block is the branch-info control. ONE affordance:
            a chevron after the name, the same signal a list row uses. The old
            tinted "More about this place" pill has gone — it duplicated this tap
            target and, tinted with the merchant's accent, outshouted the
            merchant's own name. */}
        <div
          className={cn(
            'min-w-0 flex-1 px-1',
            chow && 'px-0',
            // As a tap target this must clear 44px like everything else in the
            // header; `justify-center` keeps the two lines optically where they
            // were, so only the reachable box grows.
            onBranchInfo &&
              'flex min-h-11 cursor-pointer flex-col justify-center rounded-lg -mx-1 px-2 py-1 transition-colors hover:bg-[var(--color-bg-surface-hover)]',
            // Chow's header is the app's plain `<Header>` row: the name is the
            // page title, not a bordered control. Keep the tap target for the
            // branch sheet, but draw nothing that the app does not.
            chow && onBranchInfo && 'mx-0 px-0 py-0 hover:bg-transparent',
            onBranchInfo &&
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
          )}
          onClick={onBranchInfo}
          role={onBranchInfo ? 'button' : undefined}
          aria-label={onBranchInfo ? `${title} — more about this place` : undefined}
          tabIndex={onBranchInfo ? 0 : undefined}
          onKeyDown={
            onBranchInfo
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onBranchInfo();
                  }
                }
              : undefined
          }
        >
          <p className={cn('flex min-w-0 items-center gap-1', chow ? 'font-[family-name:var(--font-chow-display)] text-[22px] font-bold leading-7 tracking-normal' : 'text-sm font-semibold leading-tight')}>
            <span
              className={cn(
                'truncate',
                onBranchInfo && chow && 'underline decoration-1 underline-offset-[5px] decoration-current/50',
              )}
            >
              {title}
            </span>
            {onBranchInfo ? (
              <ChevronRight aria-hidden="true" className={cn('size-4 shrink-0', chow ? 'opacity-70' : 'opacity-40')} />
            ) : null}
          </p>
          {!chow && subtitle ? (
            <p className="truncate text-xs leading-tight text-[var(--color-text-secondary)]">
              {subtitle}
            </p>
          ) : null}
        </div>

        {trailing}

        {/* Search and You: the only two places a single-screen menu can go.
            Discovery ("Sparkles") has gone from the header — it opened a sheet
            of recommendations that a guest could not have known existed, and it
            competed for the corner where people look for settings. It is reached
            from the You sheet instead. */}
        {onSearch ? (
          <button
            type="button"
            onClick={onSearch}
            aria-label={chow ? (searchOpen ? 'Close menu filters' : 'Open menu filters') : 'Search the menu'}
            aria-expanded={chow ? searchOpen : undefined}
            className={cn(placeIconButton, chow && 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]')}
          >
            {chow && searchOpen ? (
              <X aria-hidden="true" className="size-5" />
            ) : chow ? (
              <SlidersHorizontal aria-hidden="true" className="size-5" />
            ) : (
              <Search aria-hidden="true" className="size-5" />
            )}
          </button>
        ) : null}

        {onOrders && !chow ? (
          <button
            type="button"
            onClick={onOrders}
            aria-label={ordersBadge > 0 ? `Your orders (${ordersBadge})` : 'Your orders'}
            className={cn(placeIconButton, 'relative', chow && 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]')}
          >
            <ReceiptText aria-hidden="true" className="size-5" />
            {ordersBadge > 0 ? (
              <span
                aria-hidden="true"
                className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-accent)] px-1 text-[10px] font-bold leading-none text-[var(--color-accent-foreground)]"
              >
                {ordersBadge > 9 ? '9+' : ordersBadge}
              </span>
            ) : null}
          </button>
        ) : null}

        {onAccount ? (
          <button
            type="button"
            onClick={onAccount}
            aria-label={chow ? 'Your account and orders' : 'Your account'}
            title={chow ? 'Your account and orders' : undefined}
            className={cn(placeIconButton, chow && 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]')}
          >
            <User aria-hidden="true" className="size-5" />
          </button>
        ) : null}
      </div>

      {!chow ? (
        modeSwitcher ? (
          <PlaceModeSwitcher
            label={modeSwitcher.label}
            detail={modeSwitcher.detail}
            modes={modes}
            onModeChange={onModeChange}
            currentModeKey={currentModeKey}
          />
        ) : showStatus ? (
          <PlaceHeaderStatus
            status={status!}
            basePath={basePath}
            onAction={onStatusAction}
            modes={modes}
            onModeChange={onModeChange}
            currentModeKey={currentModeKey}
          />
        ) : null
      ) : null}
    </header>
  );
}

/**
 * The header's second line. Same sentence the old ribbon carried, in a third of
 * the vertical space and attached to the identity it describes rather than
 * floating as its own band.
 *
 * When the branch offers more than one fulfilment mode, this line is also the
 * mode switcher: it already says the current mode ("Pickup · order ahead…"), so
 * tapping it to change is the same gesture in the same place — no separate
 * control to discover, and no second picker competing with the action bar's
 * "How would you like this?" (which only appears while the question is
 * unanswered).
 */
function PlaceHeaderStatus({
  status,
  basePath,
  onAction,
  modes,
  onModeChange,
  currentModeKey,
}: {
  status: PlaceState;
  basePath: string;
  onAction?: (action: string) => void;
  modes?: PlaceModeOption[] | null;
  onModeChange?: (key: string) => void;
  currentModeKey?: string | null;
}) {
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const modeTriggerRef = useRef<HTMLButtonElement>(null);

  const isModeChange = status.action === 'changeMode';
  const modeChangeable = isModeChange && Boolean(modes?.length) && Boolean(onModeChange);
  const interactive = (Boolean(status.action && onAction) && !isModeChange) || Boolean(status.href) || modeChangeable;

  const body = (
    <span className="mx-auto flex w-full items-center gap-1.5 [max-width:var(--place-measure,100%)]">
      <span
        aria-hidden="true"
        className={cn(
          'size-1.5 shrink-0 rounded-full',
          status.tone === 'neutral' && 'bg-[var(--color-text-secondary)] opacity-50',
          status.tone === 'live' && 'bg-[var(--color-accent)]',
          status.tone === 'attention' && 'bg-[var(--color-accent)] motion-safe:animate-pulse',
        )}
      />
      <span className="truncate font-medium">{status.label}</span>
      {status.detail ? (
        <>
          <span aria-hidden="true" className="shrink-0 opacity-30">
            ·
          </span>
          <span
            className={cn(
              'truncate',
              /\d/.test(status.detail) && 'font-[family-name:var(--font-place-data)] tabular-nums',
            )}
          >
            {status.detail}
          </span>
        </>
      ) : null}
      {interactive ? (
        <ChevronRight aria-hidden="true" className="ml-auto size-3.5 shrink-0 opacity-40" />
      ) : null}
    </span>
  );

  const shape = cn(
    'relative flex h-[22px] w-full items-center px-4 pb-1 text-[11px]',
    // The status line is deliberately 22px so the sticky header height never changes.
    // The hit area still has to be finger-sized: an invisible ::after expands the tap
    // target to 44px without adding to layout height (WCAG 2.5.5 / QA 2026-10-20).
    'after:absolute after:inset-x-0 after:-top-[11px] after:bottom-[-11px] after:content-[""]',
    'text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]',
  );

  // The mode picker, floated under the status line. Absolutely positioned so
  // the header's height never changes (sticky offsets depend on it) and the
  // menu below is simply covered while the guest decides.
  const modePicker = modeChangeable && modePickerOpen ? (
    <ModePickerMenu
      modes={modes ?? []}
      currentModeKey={currentModeKey}
      onModeChange={(key) => onModeChange?.(key)}
      onClose={() => setModePickerOpen(false)}
      triggerRef={modeTriggerRef}
    />
  ) : null;

  if (!interactive) {
    return (
      <div className={shape} data-place-status={status.stance}>
        {body}
      </div>
    );
  }

  if (modeChangeable) {
    return (
      <div className="relative">
        <button
          ref={modeTriggerRef}
          type="button"
          data-place-status={status.stance}
          aria-expanded={modePickerOpen}
          aria-haspopup="menu"
          aria-label={`Change fulfilment mode: ${status.label}${status.detail ? `, ${status.detail}` : ''}`}
          onClick={() => setModePickerOpen((v) => !v)}
          className={cn(shape, 'text-left transition-colors hover:bg-[var(--color-bg-surface-hover)]')}
        >
          {body}
        </button>
        {modePicker}
      </div>
    );
  }

  if (status.action && onAction) {
    return (
      <button
        type="button"
        data-place-status={status.stance}
        onClick={() => onAction(status.action!)}
        className={cn(shape, 'text-left transition-colors hover:bg-[var(--color-bg-surface-hover)]')}
      >
        {body}
      </button>
    );
  }

  return (
    <Link
      href={`${basePath}${status.href}`}
      data-place-status={status.stance}
      className={cn(shape, 'transition-colors hover:bg-[var(--color-bg-surface-hover)]')}
    >
      {body}
    </Link>
  );
}

/**
 * The header's second line when the guest has picked a fulfilment mode and the
 * branch offers more than one. It states the current mode and is tappable to
 * switch — the persistent "change mode" control, present whether or not the
 * cart has items (the action bar only asks the question while the cart is
 * empty, so this is the one place the choice can always be revisited).
 */
function PlaceModeSwitcher({
  label,
  detail,
  modes,
  onModeChange,
  currentModeKey,
}: {
  label: string;
  detail?: string | null;
  modes?: PlaceModeOption[] | null;
  onModeChange?: (key: string) => void;
  currentModeKey?: string | null;
}) {
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const modeTriggerRef = useRef<HTMLButtonElement>(null);
  const changeable = Boolean(modes?.length) && Boolean(onModeChange);

  const body = (
    <span className="mx-auto flex w-full items-center gap-1.5 [max-width:var(--place-measure,100%)]">
      <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-[var(--color-accent)]" />
      <span className="truncate font-medium text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]">{label}</span>
      {detail ? (
        <>
          <span aria-hidden="true" className="shrink-0 opacity-30">
            ·
          </span>
          <span className="truncate opacity-70 text-[var(--color-text-secondary)] dark:text-[var(--color-text-secondary)]">{detail}</span>
        </>
      ) : null}
      {changeable ? (
        <ChevronRight aria-hidden="true" className="ml-auto size-3.5 shrink-0 opacity-40" />
      ) : null}
    </span>
  );

  const shape = cn(
    'relative flex h-[22px] w-full items-center px-4 pb-1 text-[11px]',
    // The status line is deliberately 22px so the sticky header height never changes.
    // The hit area still has to be finger-sized: an invisible ::after expands the tap
    // target to 44px without adding to layout height (WCAG 2.5.5 / QA 2026-10-20).
    'after:absolute after:inset-x-0 after:-top-[11px] after:bottom-[-11px] after:content-[""]',
    'text-[var(--color-text-primary)] dark:text-[var(--color-text-primary)]',
  );

  const modePicker = changeable && modePickerOpen ? (
    <ModePickerMenu
      modes={modes ?? []}
      currentModeKey={currentModeKey}
      onModeChange={(key) => onModeChange?.(key)}
      onClose={() => setModePickerOpen(false)}
      triggerRef={modeTriggerRef}
    />
  ) : null;

  if (!changeable) {
    return (
      <div className={shape} data-place-status="browsing">
        {body}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        ref={modeTriggerRef}
        type="button"
        data-place-status="browsing"
        aria-expanded={modePickerOpen}
        aria-haspopup="menu"
        aria-label={`Change fulfilment mode: ${label}${detail ? `, ${detail}` : ''}`}
        onClick={() => setModePickerOpen((v) => !v)}
        className={cn(shape, 'text-left transition-colors hover:bg-[var(--color-bg-surface-hover)]')}
      >
        {body}
      </button>
      {modePicker}
    </div>
  );
}

/**
 * Does the header's second line have anything to say?
 *
 * THE DIVISION OF LABOUR: the header says WHERE YOU ARE, the action bar says
 * WHAT HAPPENS NEXT. So this line carries only the facts a guest cannot work
 * out from the screen in front of them and cannot act on from here:
 *
 *   seated / counter — which table the QR put them at, and where taps go
 *   closed           — and, crucially, when the place opens again
 *   changeMode       — the guest picked a fulfilment mode and the branch
 *                      offers more than one, so the line becomes the switcher
 *
 * Everything else is deliberately absent. `browsing` is the resolver saying
 * "nothing is happening", and a permanent strip announcing that teaches the
 * guest to ignore the place where real news will later appear — which is why
 * the old ribbon's most common content was the two words "Open now". And
 * `building` / `live_order` / `awaiting_payment` all belong to the action bar
 * at the bottom; stating them in both places is the "said three times"
 * duplication this redesign exists to remove.
 *
 * Exported because the shell publishes the header's HEIGHT from it, and a
 * second copy of this rule is exactly how the category row ended up sticking
 * 34px below the header on every menu that had no ribbon to sit under.
 */
export function placeStatusVisible(status: PlaceState | null | undefined): boolean {
  if (!status) return false;
  return (
    status.stance === 'seated' ||
    status.stance === 'counter' ||
    status.stance === 'closed' ||
    status.action === 'changeMode'
  );
}

/** The header's rendered height, as a CSS length, for anything sticking under
 *  it. Kept beside the rule that decides it. */
export function placeHeaderHeight(
  status: PlaceState | null | undefined,
  modeSwitcher?: { label: string; detail?: string | null } | null,
  chow = false,
): string {
  // Chow's header is exactly one 64px row. No status strip, no mode picker.
  if (chow) return 'calc(4rem + env(safe-area-inset-top))';
  const hasSecondLine = Boolean(modeSwitcher) || placeStatusVisible(status);
  return hasSecondLine
    ? 'calc(3.5rem + env(safe-area-inset-top) + 22px)'
    : 'calc(3.5rem + env(safe-area-inset-top))';
}

// 44px, the floor doc 05 §5.4 sets for a touch surface. These sat at 40 and
// were four of the thirteen undersized controls doc 09 §0.4 counted on a phone.
export const placeIconButton = cn(
  'inline-flex size-11 shrink-0 items-center justify-center rounded-full',
  'text-[var(--color-text-primary)] transition-colors',
  'hover:bg-[var(--color-bg-surface-hover)]',
  'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
);
